import { AsyncPipe } from "@angular/common";
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, Provider, ChangeDetectionStrategy, inject } from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";
import { NgbNav, NgbNavContent, NgbNavItem, NgbNavItemRole, NgbNavLink, NgbNavLinkBase, NgbNavOutlet } from "@ng-bootstrap/ng-bootstrap";
import { ICharacteristicMetaData } from "rl-common/models/i-characteristic-meta-data";
import { ITemplateCharacteristicGroup } from "rl-common/models/i-template-characteristic-group";
import { ISourceField } from "rl-common/services/entity-config/entity-config.models";
import { EntityConfigService } from "rl-common/services/entity-config/entity-config.service";
import { GrowlerService } from "rl-common/services/growler.service";
import { CharTypeIdUtil } from "rl-common/utils/char-type-id.util";
import { Observable, of, Subscription } from "rxjs";
import { catchError, map, switchMap, tap } from "rxjs/operators";
import { LoaderComponent } from "rl-common/components/panel/loader/loader.component";
import { TemplateLandingService } from "../../templates-landing/templates-landing.service";
import { CreateTemplateFieldWizardComponent } from "./create-template-field-wizard/create-template-field-wizard.component";
import { ICreateTemplateFormData } from "./create-template-field-wizard/template-field-policy-editor/template-field-policy-editor.models";
import { SelectTemplateSourceFieldComponent } from "./select-template-source-field/select-template-source-field.component";
import { ITemplateFieldAddComponent, TemplateFieldAddModalAdapter } from "./template-field-add.adapter";

@Component({
	selector: "rl-template-field-add",
	templateUrl: "./template-field-add.component.html",
	styleUrls: ["./template-field-add.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [NgbNav, NgbNavItem, NgbNavItemRole, NgbNavLink, NgbNavLinkBase, NgbNavContent, SelectTemplateSourceFieldComponent, CreateTemplateFieldWizardComponent, NgbNavOutlet, LoaderComponent, AsyncPipe]
})
export class TemplateFieldAddComponent implements OnInit, OnDestroy, ITemplateFieldAddComponent {
	private _templateService = inject(TemplateLandingService);
	private _entityConfigService = inject(EntityConfigService);
	private _growlerService = inject(GrowlerService);

	_subs: Subscription[] = [];
	formGroup: FormGroup<ICreateTemplateFormData>;
	isSaving = false;
	charTypeId$: Observable<number>;
	templateId$: Observable<number>;
	charTypeName$: Observable<string>;
	templateName$: Observable<string>;
	charTypeId: number;
	charTypeName: string;
	templateName: string;
	charValueSourceId: string;
	charValueSetId: string;
	conditionalTriggerFieldId: FormControl<number>;
	formLoaded = false;
	title = "";
	activeTabId = 1;
	unUsed = -1;
	notAssigned: ISourceField[] = [];
	groups: ITemplateCharacteristicGroup[] = [];
	/** The fields ticked on Select Existing, kept so a Save on Create New adds them too. */
	tickedFieldIds = new Set<number>();

	@Input()
	group: ITemplateCharacteristicGroup;

	@Input()
	templateFieldModel: ICharacteristicMetaData;

	@Input()
	charMetaData: ICharacteristicMetaData[];

	@Input()
	templateId: number;

	@Output()
	onComplete = new EventEmitter<boolean>();

	modalLoaded = false;
	sourceFieldsLoaded = false;

	get isCreate() {
		return !this.templateFieldModel;
	}

	get isEdit() {
		return !!this.templateFieldModel;
	}

	ngOnInit(): void {
		if (!this.templateId) {
			this._templateService.charTypeTemplateIds$.pipe(
				map(([_, templateId]) => templateId),
				tap(templateId => {
					if (!this.templateId) {
						this.templateId = templateId;
					}
				})
			).subscribe();
		} else if (this.templateFieldModel && !this.charTypeId) {
			this.charTypeId = this.templateFieldModel.charTypeID;
			this.charTypeName = this.charTypeId ? CharTypeIdUtil.toReportLabel(this.charTypeId) : null;
		} else if (!this.templateFieldModel && !this.charTypeId) {
			this._templateService.charTypeTemplateIds$.pipe(
				map(([charTypeId, _]) => charTypeId),
				tap(charTypeId => {
					if (!this.charTypeId) {
						this.charTypeId = charTypeId;
						this.charTypeName = this.charTypeId ? CharTypeIdUtil.toReportLabel(this.charTypeId) : null;
					}
				})
			).subscribe();
		}

		// TODO: we should merge these requests into a single endpoint to reduce round trips to the API
		const sub = this._entityConfigService.getTemplateMetaData(this.charTypeId, this.templateId).pipe(
			tap(results => {
				this.groups = results.groups;
				this.templateName = results.template.templateName;
				this.charMetaData = results.characteristicMetaDatas ?? [];
				this.setTitle();
			}),
			switchMap(() => this._entityConfigService.getSourceFields(this.charTypeId, this.unUsed)),
			tap((result) => {
				const sourceFields = result.sort((a, b) => (a.sourceFieldName.localeCompare(b.sourceFieldName)));
				this.notAssigned = sourceFields.filter(x => !this.charMetaData.find(y => y.characteristicID === x.sourceFieldID));
				this.sourceFieldsLoaded = true;
			})
		).subscribe();

		this.modalLoaded = true;
		this._subs.push(sub);
	}

	setTitle() {
		const groupLabel = this.group.groupLabel;
		if (this.isCreate) {
			this.title = `Add Fields to ${this.charTypeName} > ${this.templateName} > <strong>${groupLabel}</strong>`;
		} else {
			this.title = `Edit Template Field - ${this.charTypeName} > ${this.templateName} > ${groupLabel} > <strong>${this.templateFieldModel.label}</strong>`;
		}
	}

	onCharValueSourceIdChanged(charValueSourceId: string) {
		this.charValueSourceId = charValueSourceId;
	}

	onCharValueSetIdChanged(charValueSetId: string) {
		this.charValueSetId = charValueSetId;
	}

	onSelectionChange(fieldIds: Set<number>) {
		this.tickedFieldIds = fieldIds;
	}

	/** Select Existing's Save. A Create New wizard that hasn't been saved isn't a field yet, so only the ticked fields are added. */
	async save(fields: Set<number>) {
		await this._entityConfigService.associateCharacteristicListToTemplate(this.charTypeId, this.templateId, this.group.groupID, Array.from(fields)).toPromise();
		this.onComplete.next(true);
	}

	/** The wizard's field exists by now, so a failed association still re-reads the template and closes, saying which part didn't save. */
	saveWizard() {
		const sub = this.associateTickedFields().pipe(
			catchError(() => {
				this._growlerService.error().growl("The new field was saved, but the selected existing fields could not be added. Please add them again.");
				return of(null);
			}),
			switchMap(() => this._entityConfigService.getTemplateMetaData(this.charTypeId, this.templateId)),
			tap(metaData => {
				this._templateService.templateCharacteristicMetaDatas$.next(metaData.characteristicMetaDatas);
				this._templateService.characteristicMetaDatas = [...metaData.characteristicMetaDatas];
				this.onComplete.next(true);
			})
		).subscribe();

		this._subs.push(sub);
	}

	private associateTickedFields(): Observable<unknown> {
		if (this.tickedFieldIds.size === 0) {
			return of(null);
		}
		return this._entityConfigService.associateCharacteristicListToTemplate(this.charTypeId, this.templateId, this.group.groupID, Array.from(this.tickedFieldIds));
	}

	cancel() {
		this.onComplete.next(null);
	}

	ngOnDestroy(): void {
		this._subs.forEach(sub => sub.unsubscribe());
	}
}

export const TEMPLATEFIELDADD_MODAL_PROVIDER: Provider = {
	provide: TemplateFieldAddModalAdapter,
	useFactory: () => new TemplateFieldAddModalAdapter(TemplateFieldAddComponent)
};

